import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button, Input, LoadingSpinner } from '../components';
import { authService } from '../services';
import stanchions from '../assets/img/stanchions-disco-sm.png';

/**
 * LoginPage - The Gate (Screen 02)
 * Design: Black background, white form, minimalist
 * Purpose: User authentication entry point
 */
export default function LoginPage() {
  const location = useLocation();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Get redirect path from location state, default to guest dashboard
  const from = location.state?.from?.pathname || '/guest';

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user types
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      // Use authService for login
      const result = await authService.login(formData.email, formData.password);

      if (result.success) {
        // Redirect to dashboard or intended destination
        // The authService already stored the tokens, so we just need to reload
        window.location.href = from;
      } else {
        setError(result.error || 'Invalid email or password');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex flex-col items-center justify-center p-4 md:p-8">
      {/* Back to landing */}
      <Link
        to="/"
        className="absolute top-8 left-8 font-mono text-sm uppercase tracking-wider text-gray-400 hover:text-white transition-colors"
      >
        &larr; Back
      </Link>

      {/* Login form container */}
      <div className="w-full max-w-md">
        {/* Header with Stanchion Illustration */}
        <div className="text-center mb-12">
          <img src={stanchions} alt="Stanchions" className="mx-auto h-28" />
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white p-8 md:p-12">
          {/* Error message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border-2 border-red-600">
              <p className="font-mono text-sm text-red-600 uppercase tracking-wider">
                {error}
              </p>
            </div>
          )}

          {/* Email field */}
          <div className="mb-6">
            <Input
              label="Email"
              id="email"
              name="email"
              type="email"
              placeholder="your@email.com"
              value={formData.email}
              onChange={handleChange}
              required
              disabled={isLoading}
            />
          </div>

          {/* Password field */}
          <div className="mb-8">
            <Input
              label="Password"
              id="password"
              name="password"
              type="password"
              placeholder="Enter your password"
              value={formData.password}
              onChange={handleChange}
              required
              disabled={isLoading}
            />
          </div>

          {/* Login button */}
          <Button
            type="submit"
            variant="primary"
            fullWidth
            disabled={isLoading}
          >
            {isLoading ? (
              <span className="flex items-center justify-center gap-2">
                <LoadingSpinner size="sm" />
                Authenticating...
              </span>
            ) : (
              'Login'
            )}
          </Button>

          {/* Forgot password link */}
          <div className="mt-6 text-center">
            <Link
              to="/forgot-password"
              className="font-mono text-sm uppercase tracking-wider text-gray-500 hover:text-black transition-colors"
            >
              Forgot password?
            </Link>
          </div>
        </form>

        {/* Sign up link */}
        <div className="mt-8 text-center">
          <p className="font-mono text-sm uppercase tracking-wider text-gray-400">
            New to the party?{' '}
            <Link
              to="/register"
              className="text-white hover:underline"
            >
              Sign up
            </Link>
          </p>
        </div>
      </div>

      {/* Decorative corners */}
      <div className="absolute top-8 right-8 w-16 h-16 border-r-2 border-t-2 border-white opacity-20" />
      <div className="absolute bottom-8 left-8 w-16 h-16 border-l-2 border-b-2 border-white opacity-20" />
      <div className="absolute bottom-8 right-8 w-16 h-16 border-r-2 border-b-2 border-white opacity-20" />
    </div>
  );
}
